import { ProjectPhase } from "../../../constants/project-phase";
import { ProjectDatesSchema, SelectData } from "../../../models";
import { camelCaseToSentenceCase, sentenceOrPascalToCamel } from "../../../utils";

const PHASE_DATE_SCHEMA_KEY_MAP: Record<ProjectPhase, keyof ProjectDatesSchema> = {
  [ProjectPhase.feasibility]: "feasibility",
  [ProjectPhase.design]: "design",
  [ProjectPhase.implementation]: "implementation",
  [ProjectPhase.ongoingMonitoring]: "ongoingMonitoring",
};

const PHASE_START_END_SELECT_OPTIONS: Record<ProjectPhase, SelectData> = {
  [ProjectPhase.feasibility]: [
    { key: "Creation date", value: "Creation date" },
    { key: "Feasibility start date", value: "Feasibility start date" },
    { key: "Feasibility end date", value: "Feasibility end date" },
  ],
  [ProjectPhase.design]: [
    { key: "Design start date", value: "Design start date" },
    { key: "Design end date", value: "Design end date" },
  ],
  [ProjectPhase.implementation]: [
    { key: "Implementation start date", value: "Implementation start date" },
    { key: "Implementation end date", value: "Implementation end date" },
  ],
  [ProjectPhase.ongoingMonitoring]: [
    { key: "Ongoing monitoring start date", value: "Ongoing monitoring start date" },
    { key: "Ongoing monitoring end date", value: "Ongoing monitoring end date" },
  ],
};

const EXCLUDED_SCHEMA_PROPERTIES = new Set([
  "completedByUserUuid",
  "cycles",
  "duration",
  "end",
  "overallEnd",
  "overallStart",
  "overallStatus",
  "start",
  "status",
  "subsequentFrequency",
]);

const isPhaseKeyDateProperty = (propertyName: string): boolean => {
  return !EXCLUDED_SCHEMA_PROPERTIES.has(propertyName) && propertyName.toLowerCase().endsWith("date");
};

const getDateSchemas = (dateSchema: ProjectDatesSchema | ProjectDatesSchema[] | undefined): ProjectDatesSchema[] => {
  if (dateSchema === undefined) {
    return [];
  }

  if (Array.isArray(dateSchema)) {
    return dateSchema;
  }

  return [dateSchema];
};

export const getPhaseKeyDatesSelectDataFromDateSchema = (
  phase: string | undefined,
  dateSchema: ProjectDatesSchema | ProjectDatesSchema[] | undefined,
  phaseKeyDates?: string[]
): SelectData => {
  if (!phase) return [];

  const phaseSchemaKey = PHASE_DATE_SCHEMA_KEY_MAP[phase as ProjectPhase];
  const schemas = getDateSchemas(dateSchema);
  const queryDrivenSelectData =
    phaseKeyDates?.map((keyDate) => {
      const sentenceCaseKeyDate = camelCaseToSentenceCase(sentenceOrPascalToCamel(keyDate));
      return {
        key: keyDate,
        value: `${sentenceCaseKeyDate === "Start" || sentenceCaseKeyDate === "End" ? `Stage ${keyDate}` : sentenceCaseKeyDate}`,
      };
    }) ?? [];

  const schemaDrivenSelectData = schemas.flatMap((schema) => {
    const phaseProperties = schema?.[phaseSchemaKey]?.properties as Record<string, unknown> | undefined;
    return Object.keys(phaseProperties ?? {})
      .filter(isPhaseKeyDateProperty)
      .map((propertyName) => {
        const label = camelCaseToSentenceCase(propertyName);
        return { key: label, value: label };
      });
  });

  const fullSelectData = [
    ...queryDrivenSelectData,
    ...schemaDrivenSelectData,
    ...(PHASE_START_END_SELECT_OPTIONS[phase as ProjectPhase] ?? []),
  ];
  return fullSelectData.filter(
    (option, index, data) => data.findIndex((candidate) => candidate.key === option.key) === index
  ) as SelectData;
};
